import React, { useState, useEffect, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import {
  Container,
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Backdrop,
  Alert,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

import useFeedbackModal from '../../hooks/useFeedbackModal';
import usePersonSync from './hooks/usePersonSync';
import { getNotaByIdNuevo, editNotaAutorizacionNuevo } from '../../services/notasDeAutorizacionNuevo.service';

import CoordinadorSection from './components/CoordinadorSection';
import AutorizadorSection from './components/AutorizadorSection';
import DynamicPersonSection from './components/DynamicPersonSection';
import CursosSection from './components/CursosSection';

const EditarNotaAutorizacion = ({ notaId, onCancel, onSuccess }) => {
  const { showSuccess, showError } = useFeedbackModal();
  const {
    formData,
    setFormData,
    searchPerson,
    updateContactForCuil,
    registerPersonData,
    getOtherRoles,
  } = usePersonSync();

  const [loadingNota, setLoadingNota] = useState(true);
  const [errorCarga, setErrorCarga] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [wasSubmitted, setWasSubmitted] = useState(false);

  // Referencias para autofoco si faltan datos obligatorios al guardar
  const autCuilRef = useRef(null);
  const autCargoRef = useRef(null);
  const autMailRef = useRef(null);
  const cursosSectionRef = useRef(null);
  const coordCuilRef = useRef(null);
  const coordMailRef = useRef(null);
  const publicoInputRef = useRef(null);
  const ventajasInputRef = useRef(null);
  const tutoresSectionRef = useRef(null);
  const sgaSectionRef = useRef(null);
  const areaCorreoInputRef = useRef(null);
  const comunicacionInputRef = useRef(null);

  // Cargar datos de la nota existente
  const cargarNota = useCallback(async () => {
    if (!notaId) return;
    setLoadingNota(true);
    setErrorCarga(null);
    try {
      const nota = await getNotaByIdNuevo(notaId);

      const estadoActual =
        nota.estado_nota_autorizacion_cod ||
        (nota.cambios_estados && Array.isArray(nota.cambios_estados) && nota.cambios_estados.length > 0
          ? (nota.cambios_estados.find((ce) => ce.fecha_hora_hasta === null)?.estado_nota_autorizacion ||
            nota.cambios_estados[nota.cambios_estados.length - 1].estado_nota_autorizacion)
          : null);

      if (estadoActual !== 'PCARGA' && estadoActual !== 'REC') {
        throw new Error(
          `Solo se pueden editar notas en estado "Pendiente Suba de Nota" (PCARGA) o "Rechazada" (REC). El estado actual es ${estadoActual || 'desconocido'}.`
        );
      }

      const areaNombre = nota.detalle_area?.nombre || nota.area_cod || '';

      const coordinador = nota.detalle_coordinador || {};
      const coordData = {
        cuil: coordinador.cuil || nota.coordinador_cuil || '',
        nombre: coordinador.nombre || '',
        apellido: coordinador.apellido || '',
        mail: coordinador.mail || coordinador.email || '',
        celular: coordinador.celular || coordinador.telefono || '',
      };

      const aut = nota.detalle_autorizador_nuevo || {};
      const persAut = aut.detalle_persona || {};
      const autData = {
        cuil: persAut.cuil || '',
        nombre: persAut.nombre || '',
        apellido: persAut.apellido || '',
        descripcionCargo: aut.descripcion_cargo || '',
        mail: persAut.mail || persAut.email || persAut.correo || '',
        celular: persAut.celular || '',
        area: areaNombre,
      };

      const tutores = (nota.tutores_nuevo || []).map((t) => {
        const pers = t.detalle_persona || {};
        return {
          cuil: pers.cuil || t.persona_cuil || '',
          nombre: pers.nombre || '',
          apellido: pers.apellido || '',
          mail: pers.mail || pers.email || pers.correo || '',
          celular: pers.celular || '',
          rol: t.rol_tutor_cod || '',
        };
      });

      const sga = (nota.accesos_victorius || []).map((a) => {
        const pers = a.detalle_persona || {};
        return {
          cuil: pers.cuil || a.persona_cuil || '',
          nombre: pers.nombre || '',
          apellido: pers.apellido || '',
          mail: pers.mail || pers.email || pers.correo || '',
          celular: pers.celular || '',
        };
      });

      const cursos = (nota.cursos || []).map((c) => ({
        cod: c.cod,
        nombre: c.nombre,
        cupo: c.cupo,
        cantidad_horas: c.cantidad_horas,
      }));

      // Registrar personas en el registry de contactos
      [coordData, autData, ...tutores, ...sga].forEach((p) => {
        if (p.cuil) registerPersonData(p);
      });

      // Datos de contacto del área
      const areaCorreoDefault =
        nota.detalle_area?.correo ||
        '';
      const areaCelularDefault =
        nota.detalle_area?.celular ||
        '';

      // Mail de comunicación de contacto
      const comunicacionDefault =
        nota.comunicacion || autData.mail || coordData.mail || (nota.detalle_usuario_referente?.detalle_persona?.mail) || '';

      setFormData({
        area: areaNombre,
        areaCorreo: areaCorreoDefault,
        areaCelular: areaCelularDefault,
        cursos,
        coordinador: coordData,
        autorizador: autData,
        publicoObjetivo: nota.publico_general || '',
        ventajas: nota.ventajas || '',
        tutores,
        sga,
        comunicacion: comunicacionDefault,
      });
    } catch (err) {
      console.error('Error al cargar nota para edición:', err);
      setErrorCarga(err.message || 'Error al obtener la información de la nota');
    } finally {
      setLoadingNota(false);
    }
  }, [notaId, registerPersonData, setFormData]);

  useEffect(() => {
    cargarNota();
  }, [cargarNota]);

  const handleFieldChange = (field) => (e) => {
    setFormData((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleCoordinadorChange = (coordinadorData) => {
    setFormData((prev) => ({ ...prev, coordinador: coordinadorData }));
  };

  const handleAutorizadorChange = (autorizadorData) => {
    setFormData((prev) => ({ ...prev, autorizador: autorizadorData }));
  };

  const handleTutoresChange = (tutoresData) => {
    setFormData((prev) => ({ ...prev, tutores: tutoresData }));
  };

  const handleSGAChange = (sgaData) => {
    setFormData((prev) => ({ ...prev, sga: sgaData }));
  };

  const handleCursosChange = (cursosData) => {
    setFormData((prev) => ({ ...prev, cursos: cursosData }));
  };

  const handleBulletFocus = (field) => (e) => {
    const value = e.target.value;
    if (!value || value.trim() === '') {
      setFormData((prev) => ({ ...prev, [field]: '• ' }));
      setTimeout(() => {
        if (e.target) {
          e.target.selectionStart = e.target.selectionEnd = 2;
        }
      }, 0);
    }
  };

  const handleBulletKeyDown = (field) => (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const { selectionStart, selectionEnd, value } = e.target;
      const textBefore = value.substring(0, selectionStart);
      const textAfter = value.substring(selectionEnd);
      const lastNewLine = textBefore.lastIndexOf('\n');
      const currentLine = textBefore.substring(lastNewLine + 1);

      const trimmedLine = currentLine.trim();
      if (trimmedLine === '•' || trimmedLine === '') {
        if (trimmedLine === '•') {
          const startPos = lastNewLine >= 0 ? lastNewLine : 0;
          const newValue = value.substring(0, startPos) + textAfter;
          setFormData((prev) => ({ ...prev, [field]: newValue }));
          setTimeout(() => {
            if (e.target) {
              e.target.selectionStart = e.target.selectionEnd = startPos;
            }
          }, 0);
          return;
        }
      }

      const insertText = '\n• ';
      const newValue = textBefore + insertText + textAfter;
      const newPos = selectionStart + insertText.length;

      setFormData((prev) => ({ ...prev, [field]: newValue }));

      setTimeout(() => {
        if (e.target) {
          e.target.selectionStart = e.target.selectionEnd = newPos;
        }
      }, 0);
    }
  };

  const handleBulletChange = (field) => (e) => {
    let val = e.target.value;
    const hasActualText = val.replace(/[\u2022\u25E6\u2023\u2043\u2219*\-•\s]/g, '').length > 0;

    if (!hasActualText && (val === '' || val === '•' || val === '• ')) {
      setFormData((prev) => ({ ...prev, [field]: val }));
      return;
    }

    if (val.includes('\n')) {
      const lines = val.split('\n');
      const formattedLines = lines.map((line) => {
        const trimmed = line.trim();
        if (!trimmed) return '';
        if (/^[\u2022\u25E6\u2023\u2043\u2219*\-•]/.test(trimmed)) {
          return line;
        }
        return '• ' + trimmed;
      });
      val = formattedLines.join('\n');
    } else if (val && !/^[\u2022\u25E6\u2023\u2043\u2219*\-•]/.test(val.trim())) {
      val = '• ' + val;
    }

    setFormData((prev) => ({ ...prev, [field]: val }));
  };

  const getFormErrors = (data) => {
    const newErrors = {};
    const hasCleanContent = (str) =>
      (str || '').replace(/[\u2022\u25E6\u2023\u2043\u2219*\-•\s]/g, '').length > 0;
    const isValidEmail = (email) => {
      if (!email || typeof email !== 'string') return false;
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
    };

    if (data.cursos.length === 0) newErrors.cursos = 'Debe seleccionar al menos un curso.';

    // Coordinador: CUIL y Correo obligatorios
    const coordErr = {};
    if (!data.coordinador?.cuil) {
      coordErr.cuil = 'El coordinador es requerido.';
    }
    const coordMail = data.coordinador?.mail || data.coordinador?.correo || '';
    if (!coordMail.trim()) {
      coordErr.mail = 'El correo del coordinador/a es requerido.';
    } else if (!isValidEmail(coordMail)) {
      coordErr.mail = 'Formato de correo inválido.';
    }
    if (Object.keys(coordErr).length > 0) {
      newErrors.coordinador = coordErr;
    }

    // Autorizador: cuil, nombre, apellido, descripcionCargo obligatorios
    const autErr = {};
    if (!data.autorizador?.cuil) autErr.cuil = 'El CUIL del autorizador es requerido.';
    if (!data.autorizador?.nombre) autErr.nombre = 'El nombre del autorizador es requerido.';
    if (!data.autorizador?.apellido) autErr.apellido = 'El apellido del autorizador es requerido.';
    if (!data.autorizador?.descripcionCargo) autErr.descripcionCargo = 'La descripción del cargo es requerida.';
    const autMail = data.autorizador?.mail || data.autorizador?.correo || '';
    if (autMail.trim() && !isValidEmail(autMail)) {
      autErr.mail = 'Formato de correo inválido.';
    }
    if (Object.keys(autErr).length > 0) {
      newErrors.autorizador = autErr;
    }

    if (!hasCleanContent(data.publicoObjetivo)) {
      newErrors.publicoObjetivo = 'El público objetivo es requerido.';
    }

    if (!hasCleanContent(data.ventajas)) {
      newErrors.ventajas = 'Las ventajas son requeridas.';
    }

    // Validar Tutores: al menos uno con rol, cuil y mail obligatorios
    let tutorErrors = [];
    if (data.tutores.length === 0) {
      newErrors.tutores = 'Debe agregar al menos un tutor.';
    } else {
      const cuilSet = new Set();
      tutorErrors = data.tutores.map((p) => {
        const err = {};
        if (!p.cuil) {
          err.cuil = 'Requerido';
        } else if (cuilSet.has(p.cuil)) {
          err.cuil = 'CUIL duplicado en tutores';
        } else {
          cuilSet.add(p.cuil);
        }
        if (!p.rol) err.rol = 'Debe asignar un rol';

        const pMail = p.mail || p.correo || '';
        if (!pMail.trim()) {
          err.mail = 'El correo es requerido.';
        } else if (!isValidEmail(pMail)) {
          err.mail = 'Formato de correo inválido.';
        }

        return Object.keys(err).length > 0 ? err : null;
      });
    }

    // SGA: al menos 1 persona, CUIL y mail obligatorios en cada item
    let sgaErrors = [];
    if (data.sga.length === 0) {
      newErrors.sga = 'Debe agregar al menos una persona con acceso al Sistema de Gestión Académica.';
    } else {
      const sgaCuilSet = new Set();
      sgaErrors = data.sga.map((s) => {
        const err = {};
        if (!s.cuil) {
          err.cuil = 'Requerido';
        } else if (sgaCuilSet.has(s.cuil)) {
          err.cuil = 'CUIL duplicado en SGA';
        } else {
          sgaCuilSet.add(s.cuil);
        }

        const sMail = s.mail || s.correo || '';
        if (!sMail.trim()) {
          err.mail = 'El correo es requerido.';
        } else if (!isValidEmail(sMail)) {
          err.mail = 'Formato de correo inválido.';
        }

        return Object.keys(err).length > 0 ? err : null;
      });
    }

    // Área: correo obligatorio con formato email, celular opcional max 10 caracteres
    if (!data.areaCorreo || !data.areaCorreo.trim()) {
      newErrors.areaCorreo = 'El correo del área es requerido.';
    } else if (!isValidEmail(data.areaCorreo)) {
      newErrors.areaCorreo = 'Formato de correo inválido.';
    }
    if (data.areaCelular && data.areaCelular.trim().length > 10) {
      newErrors.areaCelular = 'El celular no puede tener más de 10 dígitos.';
    }

    // Comunicación: obligatorio con formato email
    if (!data.comunicacion || !data.comunicacion.trim()) {
      newErrors.comunicacion = 'El correo de comunicación es requerido.';
    } else if (!isValidEmail(data.comunicacion)) {
      newErrors.comunicacion = 'Formato de correo inválido.';
    }

    // =========================================================================
    // VALIDACIÓN DE UNICIDAD DE CORREO ENTRE DISTINTOS CUILs
    // "Dos personas distintas (distinto cuil) NO deben tener un mismo correo"
    // =========================================================================
    const allPersonsWithEmail = [];

    if (data.coordinador?.cuil && coordMail.trim() && isValidEmail(coordMail)) {
      allPersonsWithEmail.push({
        section: 'coordinador',
        index: null,
        cuil: String(data.coordinador.cuil).trim(),
        email: coordMail.trim().toLowerCase(),
      });
    }

    if (data.autorizador?.cuil && autMail.trim() && isValidEmail(autMail)) {
      allPersonsWithEmail.push({
        section: 'autorizador',
        index: null,
        cuil: String(data.autorizador.cuil).trim(),
        email: autMail.trim().toLowerCase(),
      });
    }

    if (Array.isArray(data.tutores)) {
      data.tutores.forEach((tutor, idx) => {
        const mail = tutor.mail || tutor.correo || '';
        if (tutor.cuil && mail.trim() && isValidEmail(mail)) {
          allPersonsWithEmail.push({
            section: 'tutores',
            index: idx,
            cuil: String(tutor.cuil).trim(),
            email: mail.trim().toLowerCase(),
          });
        }
      });
    }

    if (Array.isArray(data.sga)) {
      data.sga.forEach((sgaItem, idx) => {
        const mail = sgaItem.mail || sgaItem.correo || '';
        if (sgaItem.cuil && mail.trim() && isValidEmail(mail)) {
          allPersonsWithEmail.push({
            section: 'sga',
            index: idx,
            cuil: String(sgaItem.cuil).trim(),
            email: mail.trim().toLowerCase(),
          });
        }
      });
    }

    // Mapear email -> Set de CUILs que lo usan
    const emailToCuilsMap = new Map();
    allPersonsWithEmail.forEach((p) => {
      if (!emailToCuilsMap.has(p.email)) {
        emailToCuilsMap.set(p.email, new Set());
      }
      emailToCuilsMap.get(p.email).add(p.cuil);
    });

    // Detectar correos compartidos por más de 1 CUIL distinto
    let hasEmailDuplicateConflict = false;
    allPersonsWithEmail.forEach((p) => {
      const cuilsUsingEmail = emailToCuilsMap.get(p.email);
      if (cuilsUsingEmail && cuilsUsingEmail.size > 1) {
        hasEmailDuplicateConflict = true;
        const errorMsg = 'Este correo ya está asignado a otra persona con distinto CUIL.';

        if (p.section === 'coordinador') {
          newErrors.coordinador = { ...(newErrors.coordinador || {}), mail: errorMsg };
        } else if (p.section === 'autorizador') {
          newErrors.autorizador = { ...(newErrors.autorizador || {}), mail: errorMsg };
        } else if (p.section === 'tutores') {
          if (!tutorErrors[p.index]) tutorErrors[p.index] = {};
          tutorErrors[p.index].mail = errorMsg;
        } else if (p.section === 'sga') {
          if (!sgaErrors[p.index]) sgaErrors[p.index] = {};
          sgaErrors[p.index].mail = errorMsg;
        }
      }
    });

    if (hasEmailDuplicateConflict) {
      newErrors.duplicateEmails = 'Dos personas distintas (con distinto CUIL) no pueden tener el mismo correo electrónico en el formulario.';
    }

    if (tutorErrors.some((err) => err !== null)) {
      newErrors.tutoresList = tutorErrors;
    }
    if (sgaErrors.some((err) => err !== null)) {
      newErrors.sgaList = sgaErrors;
    }

    return newErrors;
  };

  const validationErrors = getFormErrors(formData);
  const isFormValid = Object.keys(validationErrors).length === 0;
  // Solo mostramos los errores en rojo si el usuario ya intentó guardar
  const errors = wasSubmitted ? validationErrors : {};

  const handleUpdate = async () => {
    setWasSubmitted(true);

    if (!isFormValid) {
      if (validationErrors.areaCorreo) {
        areaCorreoInputRef.current?.focus?.();
        areaCorreoInputRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      } else if (validationErrors.autorizador?.cuil || validationErrors.autorizador?.nombre || validationErrors.autorizador?.apellido) {
        autCuilRef.current?.focus?.();
        autCuilRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      } else if (validationErrors.autorizador?.descripcionCargo) {
        autCargoRef.current?.focus?.();
        autCargoRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      } else if (validationErrors.autorizador?.mail) {
        autCuilRef.current?.focus?.();
        autCuilRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      } else if (validationErrors.cursos) {
        cursosSectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      } else if (validationErrors.coordinador?.cuil || validationErrors.coordinador?.nombre || validationErrors.coordinador?.apellido || validationErrors.coordinador?.mail) {
        coordCuilRef.current?.focus?.();
        coordCuilRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      } else if (validationErrors.publicoObjetivo) {
        publicoInputRef.current?.focus?.();
        publicoInputRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      } else if (validationErrors.ventajas) {
        ventajasInputRef.current?.focus?.();
        ventajasInputRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      } else if (validationErrors.tutores || validationErrors.tutoresList) {
        tutoresSectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      } else if (validationErrors.sga || validationErrors.sgaList) {
        sgaSectionRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      } else if (validationErrors.comunicacion) {
        comunicacionInputRef.current?.focus?.();
        comunicacionInputRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsUpdating(true);

    try {
      const { area: _area, areaCorreo, areaCelular, ...restFormData } = formData;
      const { area: _autArea, ...restAutorizador } = formData.autorizador || {};
      const payload = {
        ...restFormData,
        area: {
          correo: (formData.areaCorreo || '').trim(),
          celular: formData.areaCelular?.trim() || null,
        },
        comunicacion: (formData.comunicacion || '').trim(),
        cursos: formData.cursos.map((c) => ({ nombre: c.nombre, cod_curso: c.cod })),
        autorizador: {
          ...restAutorizador,
          correo: formData.autorizador.mail || '',
        },
      };

      const blob = await editNotaAutorizacionNuevo(notaId, payload);
      const pdfBlob = new Blob([blob], { type: 'application/pdf' });
      const pdfUrl = URL.createObjectURL(pdfBlob);

      // 1. Abrir vista previa en nueva pestaña
      window.open(pdfUrl, '_blank');

      // 2. Descargar directamente el archivo PDF actualizado
      const cleanArea = (formData.area || 'Nota').replace(/[^a-zA-Z0-9áéíóúÁÉÍÓÚñÑ_-]/g, '_');
      const fileName = `Nota_Autorizacion_${cleanArea}_ID${notaId}_${dayjs().format('YYYYMMDD_HHmm')}.pdf`;

      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Limpiar memoria tras permitir tiempo suficiente para la visualización en la pestaña
      setTimeout(() => URL.revokeObjectURL(pdfUrl), 120000);

      await showSuccess({
        title: '¡Nota Actualizada Exitosamente!',
        description: `Se han guardado los cambios de la Nota de Autorización #${notaId}.`,
        confirmText: 'Aceptar',
      });

      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error al editar Nota de Autorización:', error);
      const isMailError = /mail|correo|email|tilde|acento/i.test(error?.message || '');

      if (isMailError) {
        await showError({
          title: 'Correo inválido',
          description: 'Revisar correo',
          confirmText: 'Aceptar',
        });
      } else {
        await showError({
          title: 'Error al Guardar Cambios',
          description: error?.message || 'Ocurrió un inconveniente al procesar la solicitud de actualización.',
          confirmText: 'Aceptar',
        });
      }
    } finally {
      setIsUpdating(false);
    }
  };

  if (loadingNota) {
    return (
      <Container maxWidth="lg" sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress size={48} />
        <Typography variant="h6" sx={{ mt: 2 }} color="text.secondary">
          Cargando datos de la Nota #{notaId}...
        </Typography>
      </Container>
    );
  }

  if (errorCarga) {
    return (
      <Container maxWidth="lg" sx={{ py: 6 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {errorCarga}
        </Alert>
        <Button variant="outlined" startIcon={<ArrowBackIcon />} onClick={onCancel}>
          Volver
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: { xs: 2, md: 4 }, borderRadius: 2 }}>
        <Box sx={{ mb: 2 }}>
          <Button variant="outlined" size="small" startIcon={<ArrowBackIcon />} onClick={onCancel} sx={{ borderRadius: 2 }}>
            Volver a la lista
          </Button>
        </Box>

        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold" color="primary">
            Editar Nota de Autorización #{notaId}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Modifique los datos erróneos o desactualizados. Al guardar, se actualizará el registro y se descargará el nuevo PDF oficial.
          </Typography>
        </Box>

        {/* 1. Área */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" color="primary" gutterBottom>
              Área
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Nombre de la repartición
                </Typography>
                <Typography variant="body1" fontWeight={500}>
                  {formData.area || '—'}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label={<span>Correo de Contacto del Área {!formData.areaCorreo?.trim() && <span style={{ color: '#E2464C' }}>*</span>}</span>}
                  type="email"
                  variant="outlined"
                  inputRef={areaCorreoInputRef}
                  value={formData.areaCorreo || ''}
                  onChange={handleFieldChange('areaCorreo')}
                  error={!!errors.areaCorreo}
                  helperText={errors.areaCorreo || 'Ej: contacto.area@cordoba.gob.ar'}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Celular de Contacto (opcional)"
                  variant="outlined"
                  value={formData.areaCelular || ''}
                  onChange={(e) => {
                    const clean = e.target.value.replace(/\D/g, '').slice(0, 10);
                    setFormData((prev) => ({ ...prev, areaCelular: clean }));
                  }}
                  error={!!errors.areaCelular}
                  helperText={errors.areaCelular || 'Máximo 10 dígitos (ej: 3511234567)'}
                  inputProps={{
                    maxLength: 10,
                    inputMode: 'numeric',
                    pattern: '[0-9]*'
                  }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* 2. Autorizador */}
        <AutorizadorSection
          value={formData.autorizador}
          onChange={handleAutorizadorChange}
          onSearchPerson={searchPerson}
          onContactChange={updateContactForCuil}
          otherRoles={getOtherRoles(formData.autorizador?.cuil, 'autorizador')}
          error={errors.autorizador}
          cuilInputRef={autCuilRef}
          cargoInputRef={autCargoRef}
          mailInputRef={autMailRef}
        />

        {/* 3. Cursos */}
        <CursosSection
          selectedCursos={formData.cursos}
          onChange={handleCursosChange}
          error={errors.cursos}
          containerRef={cursosSectionRef}
          currentNotaId={notaId}
        />

        {/* 4. Coordinador */}
        <CoordinadorSection
          value={formData.coordinador}
          onChange={handleCoordinadorChange}
          onSearchPerson={searchPerson}
          onContactChange={updateContactForCuil}
          otherRoles={getOtherRoles(formData.coordinador?.cuil, 'coordinador')}
          error={errors.coordinador}
          cuilInputRef={coordCuilRef}
          mailInputRef={coordMailRef}
        />

        {/* 5. Público Objetivo y Ventajas */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" color="primary" gutterBottom>
              Detalles de la Nota de Autorización
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label={<span>Público Objetivo {!formData.publicoObjetivo?.replace(/[\u2022\u25E6\u2023\u2043\u2219*\-•\s]/g, '').length && <span style={{ color: '#E2464C' }}>*</span>}</span>}
                  variant="outlined"
                  inputRef={publicoInputRef}
                  value={formData.publicoObjetivo}
                  onFocus={handleBulletFocus('publicoObjetivo')}
                  onKeyDown={handleBulletKeyDown('publicoObjetivo')}
                  onChange={handleBulletChange('publicoObjetivo')}
                  error={!!errors.publicoObjetivo}
                  helperText={errors.publicoObjetivo || 'Presione Enter para agregar un nuevo ítem/viñeta'}
                  placeholder="• Describa el público al que está dirigida la capacitación..."
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label={<span>Ventajas / Beneficios {!formData.ventajas?.replace(/[\u2022\u25E6\u2023\u2043\u2219*\-•\s]/g, '').length && <span style={{ color: '#E2464C' }}>*</span>}</span>}
                  variant="outlined"
                  inputRef={ventajasInputRef}
                  value={formData.ventajas}
                  onFocus={handleBulletFocus('ventajas')}
                  onKeyDown={handleBulletKeyDown('ventajas')}
                  onChange={handleBulletChange('ventajas')}
                  error={!!errors.ventajas}
                  helperText={errors.ventajas || 'Presione Enter para agregar un nuevo ítem/viñeta'}
                  placeholder="• Detalle los beneficios y justificación de esta capacitación..."
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* 6. Tutores */}
        <Box sx={{ mb: 3 }}>
          <DynamicPersonSection
            title="Tutores"
            description="Agregue a los tutores asignados. Es obligatorio cargar al menos uno e indicarle su nivel de permisos."
            sectionKey="tutores"
            persons={formData.tutores}
            onChange={handleTutoresChange}
            onSearchPerson={searchPerson}
            onContactChange={updateContactForCuil}
            getOtherRoles={getOtherRoles}
            showRole={true}
            showContact={true}
            errors={errors.tutoresList}
            sectionError={errors.tutores}
            containerRef={tutoresSectionRef}
          />
        </Box>

        {/* 7. Accesos SGA */}
        <Box sx={{ mb: 3 }}>
          <DynamicPersonSection
            title="Accesos para Sistema de Gestión Académica (SGA)"
            description="Estas personas se encargarán de tareas como la 'Creación de Instancias (cohortes)' y 'Gestión de inscripciones'. Es obligatorio agregar al menos una persona."
            sectionKey="sga"
            persons={formData.sga}
            onChange={handleSGAChange}
            onSearchPerson={searchPerson}
            onContactChange={updateContactForCuil}
            getOtherRoles={getOtherRoles}
            showRole={false}
            showContact={true}
            errors={errors.sgaList}
            sectionError={errors.sga}
            containerRef={sgaSectionRef}
          />
        </Box>

        {/* 8. Comunicación */}
        <Card variant="outlined" sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" color="primary" gutterBottom>
              Comunicación
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Correo electrónico de contacto para atención a la ciudadanía.
            </Typography>
            <TextField
              fullWidth
              label={<span>Correo de Contacto {!formData.comunicacion?.trim() && <span style={{ color: '#E2464C' }}>*</span>}</span>}
              type="email"
              variant="outlined"
              inputRef={comunicacionInputRef}
              value={formData.comunicacion}
              onChange={handleFieldChange('comunicacion')}
              error={!!errors.comunicacion}
              helperText={errors.comunicacion || 'Ej: consultas@cordoba.gob.ar'}
            />
          </CardContent>
        </Card>



        {/* Alerta de correos duplicados si aplica */}
        {errors.duplicateEmails && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {errors.duplicateEmails}
          </Alert>
        )}

        {/* Submit */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
          <Button variant="outlined" size="large" onClick={onCancel} sx={{ px: 4, py: 1.5 }}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={<PictureAsPdfIcon />}
            onClick={handleUpdate}
            disabled={isUpdating}
            sx={{ px: 5, py: 1.5, fontSize: '1.1rem', fontWeight: 'bold' }}
          >
            {isUpdating ? 'Guardando y Regenerando...' : 'Guardar'}
          </Button>
        </Box>
      </Paper>

      {/* Backdrop loading overlay */}
      <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isUpdating}>
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress color="inherit" size={60} />
          <Typography variant="h6" sx={{ mt: 2, color: 'white' }}>
            Guardando cambios y regenerando PDF...
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'rgba(255,255,255,0.7)' }}>
            Esto puede tomar unos segundos
          </Typography>
        </Box>
      </Backdrop>
    </Container>
  );
};

export default EditarNotaAutorizacion;
